import type { Pipeline } from "@/schema"

import { TRIGGER_TYPE_MAP } from "@/components/automations/lib/automation-transformer.mappings"
import type {
  AutomationTriggerType,
  FrontendTriggerDefinition,
  NodeKey,
  TriggerConfig,
} from "@/components/automations/types"

import { businessTriggers } from "./business"
import { contactTriggers } from "./contact"
import { householdTriggers } from "./household"
import { meetingTriggers } from "./meeting"
import { opportunityTriggers } from "./opportunity"
import { projectTriggers } from "./project"
import { resolveTriggerProvidedSubjects } from "./provided-subjects"
import { reminderTriggers } from "./reminder"
import { systemTriggers } from "./system"
import { taskTriggers } from "./task"

/**
 * All available trigger definitions grouped by record type
 */
export const TRIGGER_DEFINITIONS: Record<AutomationTriggerType, FrontendTriggerDefinition[]> = {
  reminder: reminderTriggers,
  household: householdTriggers,
  contact: contactTriggers,
  business: businessTriggers,
  meeting: meetingTriggers,
  task: taskTriggers,
  opportunity: opportunityTriggers,
  project: projectTriggers,
  system: systemTriggers,
  person: [],
}

/**
 * Get trigger definition by key
 */
export function getTriggerDefinition(key?: NodeKey): FrontendTriggerDefinition | undefined {
  if (!key) return undefined

  for (const triggers of Object.values(TRIGGER_DEFINITIONS)) {
    const trigger = triggers.find((t) => t.key === key)
    if (trigger) return trigger
  }
  return undefined
}

export function getTriggerProvidedSubjects(key?: NodeKey, trigger?: TriggerConfig, pipelines?: Pipeline[]) {
  const definition = getTriggerDefinition(key)
  if (!definition) return []

  return resolveTriggerProvidedSubjects(definition, trigger, pipelines)
}

/**
 * Get all triggers available for a specific record type
 */
export function getTriggerDefinitionsForRecordType(recordType: AutomationTriggerType): FrontendTriggerDefinition[] {
  return TRIGGER_DEFINITIONS[recordType] || []
}

/**
 * Get trigger label from backend type and record type
 */
export function getTriggerLabel(backendType: string, recordType: string | null, ageMilestone?: string | null): string {
  const allTriggers = Object.values(TRIGGER_DEFINITIONS).flat()

  for (const trigger of allTriggers) {
    const mappedBackendType = TRIGGER_TYPE_MAP[trigger.key]
    if (mappedBackendType === backendType) {
      if (matchesTriggerContext(trigger.key, recordType, ageMilestone)) {
        return trigger.description
      }
    }
  }

  return formatFallbackLabel(backendType)
}

function matchesTriggerContext(triggerKey: string, recordType: string | null, ageMilestone?: string | null): boolean {
  if (!recordType) return true
  if (triggerKey === "trigger__form_accepted") return recordType === "intake_form_submission"

  if (triggerKey.includes("contact") && recordType === "contact") return true
  if (triggerKey.includes("business") && recordType === "business") return true
  if (triggerKey.includes("task") && recordType === "task") return true
  if (triggerKey.includes("meeting") && recordType === "meeting") return true
  if (triggerKey.includes("opportunity") && recordType === "opportunity") return true
  if (triggerKey.includes("project") && recordType === "project") return true
  if (triggerKey === "trigger__client_touchpoint_reminder" && recordType === "client") return true
  if (triggerKey.includes("household") && ["client", "prospect", "household"].includes(recordType)) return true
  if (triggerKey.includes("email") && recordType === "email_message") return true
  if (triggerKey === "trigger__recurring" && !recordType) return true
  if (triggerKey.includes("birthday") && recordType === "person") return !ageMilestone
  if (triggerKey.includes("age_milestone") && recordType === "person") return !!ageMilestone
  if (triggerKey.includes("annual_review") && recordType === "client") return true
  if (triggerKey.includes("client_anniversary") && recordType === "client") return true
  if (triggerKey === "trigger__prospect_promoted_to_client" && recordType === "client") return true
  if (triggerKey === "trigger__client_archived_to_past_client" && recordType === "past_client") return true
  if (triggerKey === "trigger__custom_date_reminder" && ["client", "prospect"].includes(recordType)) return true

  return false
}

function formatFallbackLabel(backendType: string): string {
  return backendType
    .replace("AutomationTriggers::", "")
    .replace(/([A-Z])/g, " $1")
    .trim()
}
